import { ofType } from 'redux-observable';
import { concat, EMPTY, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, mergeMap } from 'rxjs/operators';

import { CACHE_AND_SERVICES } from '../../constants/playlists';
import { MULTI_PLAYLIST_ENDPOINT_URL, PLAYLIST_REQUEST_TIMEOUT } from '../../constants/request';
import { CHANNEL_LIST_PATH } from '../../constants/routePath';

import { getVideoEntityTypes } from '../../helpers/chips';
import { push } from '../../helpers/history';
import { getBackupPlaylistUrl, includeFeatures, tmPostRequest } from '../../helpers/request';
import { getChannelUrl } from '../../helpers/url';
import { getFormattedVideo } from '../../helpers/video';
import * as commonSelectors from '../selectors/common';
import {
  additionalVideoFetchStartedAction,
  channelsFetchSuccessAction,
  oneChannelFetchStartAction,
} from '../slices/common';

export default (action$, state$) =>
  action$.pipe(
    ofType(oneChannelFetchStartAction.type),
    mergeMap(({ payload }) => {
      const publisher = commonSelectors.publisherSelector(state$.value);
      const basePath = commonSelectors.basePathSelector(state$.value);
      const player = commonSelectors.playerSelector(state$.value);
      const tags = commonSelectors.tagsSelector(state$.value);
      const tagTypes = commonSelectors.tagTypesSelector(state$.value);
      const channels = commonSelectors.channelsSelector(state$.value);
      const isChannelExists = !!channels.find((chl) => chl.id === payload.channel.id).playlistLoaded;

      const getUpdateChannels = (responsePlaylists = []) => {
        const allChannels = commonSelectors.channelsSelector(state$.value);

        return allChannels.map((chl) => {
          const channelUrl = getChannelUrl(basePath, chl.name);
          const item = responsePlaylists.find((item$) => channelUrl === item$.url);

          if (item?.playlistData?.playlist) {
            const { playlist, errors, ...playerPropsPassThrow } = item.playlistData;

            return {
              ...chl,
              playerPropsPassThrow,
              playlist: playlist.map((video) =>
                getFormattedVideo(video, {
                  signedUrlExp: playerPropsPassThrow.signedUrlExp,
                }),
              ),
              playlistLoaded: true,
            };
          }

          return { ...chl };
        });
      };

      const actionsSteam = [];

      if (isChannelExists) {
        actionsSteam.push(
          of(channelsFetchSuccessAction(channels)),
          of(
            additionalVideoFetchStartedAction({
              video: payload.video,
              params: payload.params,
              targetChannel: payload.channel,
              querySearchString: payload.querySearchString,
            }),
          ),
        );
      } else {
        actionsSteam.push(
          tmPostRequest({
            url: MULTI_PLAYLIST_ENDPOINT_URL,
            timeout: PLAYLIST_REQUEST_TIMEOUT,
            body: {
              videoHub: true,
              publisherId: publisher.slug,
              cacheUsage: CACHE_AND_SERVICES,
              playlists: [
                {
                  url: getChannelUrl(basePath, payload.channel.name),
                  widgetId: player.name,
                },
              ],
              videoEntityTypes: tags ? getVideoEntityTypes(tagTypes) : undefined,
              ...includeFeatures(state$.value),
            },
          }).pipe(
            mergeMap(({ response }) => {
              const allChannelsAfterUpdate = getUpdateChannels(response?.playlists);
              const channel = allChannelsAfterUpdate.find((chl) => chl.id === payload.channel.id);

              const actions = [of(channelsFetchSuccessAction(allChannelsAfterUpdate))];

              if (!channel.playlist.some(({ mediaid }) => mediaid === payload.video)) {
                actions.push(
                  of(
                    additionalVideoFetchStartedAction({
                      video: payload.video,
                      params: payload.params,
                      targetChannel: payload.channel,
                      querySearchString: payload.querySearchString,
                    }),
                  ),
                );
              }

              return concat(...actions);
            }),
            catchError(() =>
              ajax({
                url: getBackupPlaylistUrl(publisher.slug, player.name, payload.channel.id),
                method: 'GET',
                crossDomain: true,
              }).pipe(
                mergeMap(({ response }) => {
                  const allChannelsAfterUpdate = getUpdateChannels(response?.playlists);

                  return of(channelsFetchSuccessAction(allChannelsAfterUpdate));
                }),
                catchError(() => {
                  push(CHANNEL_LIST_PATH);

                  return EMPTY;
                }),
              ),
            ),
          ),
        );
      }

      return concat(...actionsSteam);
    }),
  );
